import React, { useState, useContext, useEffect } from 'react';
import { connect } from 'react-redux';
import { AppContext } from '@prisjakt/ui-app-context';
import loadable from '@loadable/component';
import { InView } from 'react-intersection-observer';
import 'isomorphic-unfetch';

import BackToTopIcon from 'mdi-react/ArrowUpIcon';

import Breadcrumb from '@prisjakt/ui-breadcrumb';
import Panel from '@prisjakt/ui-panel';
import { ScrollLink, ScrollElement } from '@prisjakt/ui-scroll';

import { AppState } from '../../reducers';
import modalActions from '../../components/modal/actions';

import AdContainer from '../../components/ads';
import CampaignDeals from '../../components/campaign-deals';
import ErrorBoundary from '../../components/error-boundary';
import FeedbackButton from '../feedback/feedback-button';
import Lazy from '../../components/lazy';
import Modal from '../../components/modal';
import PageContent from '../../components/page-content';
import PreReleaseInformation from './components/pre-release-information';
import PriceHistory from './components/price-history';
import PriceList from './components/price-list';
import ProductHeader from './components/product-header';
import ProductPriceHistory from '../product-price-history';
import ProductProperties from './components/product-properties';
import PromoBanner from '../../components/campaign-banners/promo-banner';
import ReadableProductInformation from './components/readable-product-information';
import RecentlyVisitedProducts from '../../components/recently-visited';
import RelatedProducts from './components/related-products';
import StickyTrack from '../../components/sticky-track';
import TabList from './components/tab-list';
import UserReviews from './components/user-reviews';
import { ViewportContext } from '../../components/viewport';

import productPageQuery from './query.graphql';

import { PRODUCT_ALERT_MODAL_ID } from './constants';
import { getHeaderHeight, getScrollLinkOffset } from './utils';
import getCampaignColor from '../../globals/helpers/campaign/get-campaign-color';

import { MarketCodes } from '../../../config/types/market';

import { Campaign, CampaignBanner, ProductPageQuery, User } from '../../types';
import { Review, RouteSection, CurrentModal, CurrentCampaign } from './types';
import { TopProductIds } from './components/product-header/types';

import * as S from './styles';
import {
  useAlertPriceStepExperiment,
  useAlertStoreRatingExperiment,
} from '../../hooks/experiments';

const ProductAlertModalContent = loadable(() =>
  import(
    /* webpackChunkName: "extra" */ '../product-alert/components/product-alert-modal-content'
  )
);

const returnNull = () => null;

type OwnProps = {
  activeCampaign: Campaign;
  data: ProductPageQuery;
  fetchMoreData: () => void;
  routeSection: RouteSection;
};

type StateProps = {
  campaignBanners: {
    active: (CampaignBanner & {
      campaign: Pick<Campaign, 'media'>;
    })[];
  };
  currentModal: CurrentModal;
  me: User;
  review: Review;
};

type DispatchProps = {
  hideModal: () => void;
  showModal: () => void;
};

type Props = OwnProps & StateProps & DispatchProps;

const ProductPage = ({
  activeCampaign,
  data,
  fetchMoreData,
  routeSection = 'main',
  campaignBanners,
  currentModal,
  me,
  review,
  hideModal,
  showModal,
}: Props) => {
  const [isHeaderVisible, setHeaderVisible] = useState<boolean>(true);
  const [campaigns, setCampaignsState] = useState([]);
  const { product, importantDates } = data;
  const { t, linkTo, market } = useContext(AppContext);
  const showAlertPriceStep = useAlertPriceStepExperiment();
  const showAlertStoreRating = useAlertStoreRatingExperiment();

  useEffect(() => {
    if (!IS_BROWSER || !product || !product.id) {
      return;
    }

    const base = market.campaignsBaseUrl;

    fetch(`${base}/campaign-${market.code}-*-deals/_search`, {
      method: 'POST',
      body: JSON.stringify({
        _source: ['priceInfo.priceId', 'campaign.id'],
        query: {
          bool: {
            filter: {
              terms: { 'product.id': [product.id] },
            },
          },
        },
      }),
    })
      .then(res => res.json())
      .then(
        res =>
          res.hits &&
          res.hits.hits &&
          setCampaignsState(
            res.hits.hits.map((hit: any) => ({
              id: hit._source.campaign.id,
              price: { id: hit._source.priceInfo.priceId.toString() },
            }))
          )
      );
  }, [product && product.id]); // eslint-disable-line

  if (!product) return null;

  product.campaigns = campaigns;

  const privacyPolicyUri =
    market &&
    market.privacySettings &&
    market.privacySettings.responsive &&
    market.privacySettings.responsive.privacyUrl;

  const currentCampaign =
    activeCampaign &&
    product.campaigns &&
    product.campaigns
      .filter(c => c && String(c.id) === String(activeCampaign.id))
      .map(match => ({
        ...match,
        name: activeCampaign.name,
        path: `${
          activeCampaign.web_uri ? activeCampaign.web_uri.responsive : ''
        }${product.category ? `?category=${product.category.id}` : ''}`,

        media: {
          colors: {
            background: match && getCampaignColor(match.id, 'background'),
            text: match && getCampaignColor(match.id, 'text'),
          },
        },
      }))
      .find(() => true);

  const campaignPromoBanners = {
    before:
      campaignBanners &&
      campaignBanners.active.filter(b => b && b.type === 'BEFORE_PROMO_BANNER'),
    during:
      campaignBanners &&
      campaignBanners.active.filter(b => b && b.type === 'DURING_PROMO_BANNER'),
  };
  const campaignBannerLinkParams = product.category
    ? { category: product.category.id }
    : undefined;

  // TODO: Mutate product data in data container instead of contextual rendering
  const userReviews =
    review.userReviews &&
    review.reviewType &&
    review.reviewType.toLowerCase() === 'product'
      ? review.userReviews
      : product.userReviews;

  const relatedProducts = product.related && product.related.products;

  const isPreRelease =
    product.releaseDate &&
    Date.parse(product.releaseDate) > Date.parse(new Date().toString());

  const hasRelatedProducts =
    !isPreRelease && relatedProducts && relatedProducts.length > 0;

  const blurActiveElement = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  // Transform some basic information about the product into a DataList-friendly shape to pass into ProductProperties
  const basicInfo = () => {
    if (!product) {
      return [];
    }

    const { brand, category } = product;
    const brandProperties = brand
      ? [
          {
            name: t('product', 'property.brand'),
            value: {
              type: 'string',
              text: brand.name,
              link: brand.pathName,
            },
          },
        ]
      : [];
    const categoryProperties = category
      ? [
          {
            name: t('product', 'property.category'),
            value: {
              type: 'string',
              text: category.name,
              link: category.pathName,
            },
          },
        ]
      : [];

    return [
      ...[
        {
          name: t('product', 'property.product-name'),
          value: { type: 'string', text: product.name },
        },
      ],
      ...brandProperties,
      ...categoryProperties,
    ];
  };

  const onHeaderVisibilityChange = (isVisible: boolean) => {
    if (isHeaderVisible !== isVisible) {
      setHeaderVisible(isVisible);
    }
  };

  const getCategoryTopProducts = () => {
    // Returns array with current product id + top 10 product ids in same category.
    return [
      { id: product.id },
      ...product?.category?.products?.nodes.filter(
        node => node?.id !== product.id
      ),
    ] as TopProductIds;
  };

  // We want to test rendering all tabbed sections on load in DK
  // by measuring the SEO vs performance impact.
  // All tabs and rendered in the DOM but non active are hidden with CSS.
  const shouldRenderAllTabs = market.code === 'dk';

  return (
    <>
      {me && me.analyticsAccess && (
        <FeedbackButton position="right">
          {t('feedback', 'button.text')}
        </FeedbackButton>
      )}

      <PageContent withAds>
        <S.ProductPage>
          {currentModal === PRODUCT_ALERT_MODAL_ID && (
            <Modal
              id={PRODUCT_ALERT_MODAL_ID}
              onRequestClose={hideModal}
              width={500}
            >
              <ProductAlertModalContent
                productId={product.id}
                hideModal={hideModal}
                privacyPolicyUri={privacyPolicyUri || undefined}
                showAlertPriceStep={showAlertPriceStep}
                showAlertStoreRating={showAlertStoreRating}
              />
            </Modal>
          )}
          {campaignPromoBanners.during &&
            campaignPromoBanners.during.length > 0 && (
              <div className="column small-12">
                <PromoBanner
                  banners={campaignPromoBanners.during}
                  linkParams={campaignBannerLinkParams}
                />
              </div>
            )}

          <S.BreadCrumbWrapper>
            <Breadcrumb
              homeText={t('common', 'breadcrumb.home')}
              path={product.category ? product.category.path : undefined}
              firstPathSelectable
            />
          </S.BreadCrumbWrapper>
          <ScrollElement name="Top">
            <ViewportContext.Consumer>
              {({ isMobile }) => (
                <>
                  <InView
                    onChange={onHeaderVisibilityChange}
                    rootMargin={`-${getHeaderHeight(isMobile) - 10}px`}
                  >
                    {({ ref }) => (
                      <ProductHeader
                        ref={ref}
                        title={product.name}
                        product={product}
                        currentCampaign={currentCampaign}
                        showModal={showModal}
                        categoryTopProducts={getCategoryTopProducts()}
                      />
                    )}
                  </InView>

                  <ScrollLink to="Top" onClick={blurActiveElement}>
                    <S.BackToTopButton
                      aria-label={t(
                        'product',
                        'back-to-top.button-description'
                      )}
                    >
                      <BackToTopIcon />
                    </S.BackToTopButton>
                  </ScrollLink>

                  <ScrollElement name="Content">
                    <StickyTrack
                      topMargin={0}
                      headerHeight={getHeaderHeight(isMobile)}
                      renderElement={
                        <TabList
                          routeSection={routeSection}
                          product={product}
                          isSticky={!isHeaderVisible}
                        />
                      }
                    >
                      <S.TabContentWrapper spacingTop={routeSection !== 'main'}>
                        {!shouldRenderAllTabs ? (
                          <>
                            <S.TabContent id="main">
                              {routeSection === 'main' && (
                                <>
                                  {isPreRelease && product.releaseDate && (
                                    <PreReleaseInformation
                                      product={product}
                                      releaseDate={product.releaseDate}
                                    />
                                  )}
                                  {product.statistics &&
                                    product.statistics.pageInfo &&
                                    product.statistics.pageInfo.lowestPrice &&
                                    isPreRelease && (
                                      <PriceHistory
                                        route={linkTo(
                                          'productPriceHistory',
                                          product
                                        )}
                                        productName={product.name}
                                        {...product.statistics}
                                        predictedPrices={
                                          product.priceForecast
                                            ? product.priceForecast
                                            : undefined
                                        }
                                      />
                                    )}
                                  {isPreRelease &&
                                    relatedProducts &&
                                    relatedProducts.length > 0 && (
                                      <RelatedProducts
                                        resources={relatedProducts}
                                        category={product.category}
                                        product={product}
                                      />
                                    )}
                                  {product.prices && (
                                    <ScrollElement
                                      name="Pricelist"
                                      data-test="MainTabContent"
                                    >
                                      <PriceList
                                        {...product}
                                        media={product.media}
                                        productName={product.name}
                                        adOffset={1}
                                        campaign={
                                          currentCampaign as CurrentCampaign
                                        }
                                      />
                                    </ScrollElement>
                                  )}
                                  <ReadableProductInformation
                                    product={product}
                                  />
                                </>
                              )}
                            </S.TabContent>

                            <S.TabContent id="properties">
                              {routeSection === 'properties' && (
                                <ErrorBoundary onError={returnNull}>
                                  <ScrollElement
                                    name="Product Properties"
                                    data-test="PropertiesTabContent"
                                  >
                                    <ProductProperties
                                      basicInfo={basicInfo()}
                                      properties={product.properties}
                                      description={product.description}
                                      getScrollLinkOffset={getScrollLinkOffset}
                                    />
                                  </ScrollElement>
                                </ErrorBoundary>
                              )}
                            </S.TabContent>

                            <S.TabContent id="reviews">
                              {routeSection === 'reviews' && (
                                <ErrorBoundary onError={returnNull}>
                                  <ScrollElement
                                    name="Reviews"
                                    data-test="ReviewsTabContent"
                                  >
                                    <UserReviews
                                      belongsTo={{
                                        id: product.id,
                                        name: product.name,
                                        type: 'product',
                                      }}
                                      myReview={
                                        userReviews && userReviews.myReview
                                      }
                                      summary={product.userReviewSummary}
                                      nodes={userReviews && userReviews.nodes}
                                      pageInfo={
                                        userReviews && userReviews.pageInfo
                                      }
                                    />
                                  </ScrollElement>
                                </ErrorBoundary>
                              )}
                            </S.TabContent>

                            <S.TabContent id="statistics">
                              {routeSection === 'statistics' && (
                                <ErrorBoundary onError={returnNull}>
                                  <ScrollElement
                                    name="Product Information"
                                    data-test="StatisticsTabContent"
                                  >
                                    <ProductPriceHistory
                                      data={{ product, importantDates }}
                                      fetchMoreData={fetchMoreData}
                                    />
                                  </ScrollElement>
                                </ErrorBoundary>
                              )}
                            </S.TabContent>
                          </>
                        ) : (
                          <>
                            <S.TabContent id="main">
                              <S.HideableContent
                                visible={routeSection === 'main'}
                              >
                                {isPreRelease && product.releaseDate && (
                                  <PreReleaseInformation
                                    product={product}
                                    releaseDate={product.releaseDate}
                                  />
                                )}
                                {product.statistics &&
                                  product.statistics.pageInfo &&
                                  product.statistics.pageInfo.lowestPrice &&
                                  isPreRelease && (
                                    <PriceHistory
                                      route={linkTo(
                                        'productPriceHistory',
                                        product
                                      )}
                                      productName={product.name}
                                      {...product.statistics}
                                      predictedPrices={
                                        product.priceForecast
                                          ? product.priceForecast
                                          : undefined
                                      }
                                    />
                                  )}
                                {isPreRelease &&
                                  relatedProducts &&
                                  relatedProducts.length > 0 && (
                                    <RelatedProducts
                                      resources={relatedProducts}
                                      category={product.category}
                                      product={product}
                                    />
                                  )}
                                {product.prices && (
                                  <ScrollElement
                                    name="Pricelist"
                                    data-test="MainTabContent"
                                  >
                                    <PriceList
                                      {...product}
                                      adOffset={1}
                                      campaign={
                                        currentCampaign as CurrentCampaign
                                      }
                                    />
                                  </ScrollElement>
                                )}
                                <ReadableProductInformation product={product} />
                              </S.HideableContent>
                            </S.TabContent>

                            <S.TabContent id="properties">
                              <S.HideableContent
                                visible={routeSection === 'properties'}
                              >
                                <ErrorBoundary onError={returnNull}>
                                  <ScrollElement
                                    name="Product Properties"
                                    data-test="PropertiesTabContent"
                                  >
                                    <ProductProperties
                                      basicInfo={basicInfo()}
                                      properties={product.properties}
                                      description={product.description}
                                      getScrollLinkOffset={getScrollLinkOffset}
                                    />
                                  </ScrollElement>
                                </ErrorBoundary>
                              </S.HideableContent>
                            </S.TabContent>

                            <S.TabContent id="reviews">
                              <S.HideableContent
                                visible={routeSection === 'reviews'}
                              >
                                <ErrorBoundary onError={returnNull}>
                                  <ScrollElement
                                    name="Reviews"
                                    data-test="ReviewsTabContent"
                                  >
                                    <UserReviews
                                      belongsTo={{
                                        id: product.id,
                                        name: product.name,
                                        type: 'product',
                                      }}
                                      myReview={
                                        userReviews && userReviews.myReview
                                      }
                                      summary={product.userReviewSummary}
                                      nodes={userReviews && userReviews.nodes}
                                      pageInfo={
                                        userReviews && userReviews.pageInfo
                                      }
                                    />
                                  </ScrollElement>
                                </ErrorBoundary>
                              </S.HideableContent>
                            </S.TabContent>

                            <S.TabContent id="statistics">
                              <S.HideableContent
                                visible={routeSection === 'statistics'}
                              >
                                <ErrorBoundary onError={returnNull}>
                                  <ScrollElement
                                    name="Product Information"
                                    data-test="StatisticsTabContent"
                                  >
                                    <ProductPriceHistory
                                      data={{ product, importantDates }}
                                      fetchMoreData={fetchMoreData}
                                    />
                                  </ScrollElement>
                                </ErrorBoundary>
                              </S.HideableContent>
                            </S.TabContent>
                          </>
                        )}

                        <Panel>
                          <AdContainer
                            placement="panorama_target"
                            format="panorama_1_wde"
                            min={861}
                            max={10000}
                            scalable
                            scaleBase=".pj-ui-panel--row"
                            triggerPoint={100}
                          />
                        </Panel>

                        {product.category &&
                          product.category.deals &&
                          product.category.deals.length > 0 && (
                            <ErrorBoundary onError={returnNull}>
                              <Lazy offset={100}>
                                <CampaignDeals
                                  deals={product.category.deals}
                                  campaignLinkQuery={`?category=${product.category.id}`}
                                />
                              </Lazy>
                            </ErrorBoundary>
                          )}

                        <Lazy offset={100}>
                          <RecentlyVisitedProducts offset={1} />
                        </Lazy>

                        {hasRelatedProducts && (
                          <ScrollElement name="Related products">
                            <RelatedProducts
                              resources={relatedProducts}
                              category={product.category}
                              product={product}
                            />
                          </ScrollElement>
                        )}

                        {campaignPromoBanners.before &&
                          campaignPromoBanners.before.length > 0 && (
                            <PromoBanner
                              inverted
                              banners={campaignPromoBanners.before}
                            />
                          )}
                      </S.TabContentWrapper>
                    </StickyTrack>
                  </ScrollElement>
                </>
              )}
            </ViewportContext.Consumer>
          </ScrollElement>
        </S.ProductPage>
      </PageContent>
    </>
  );
};

ProductPage.graphQL = productPageQuery;

type Group = {
  match: Match;
  name: string;
};

type Match = {
  isExact: boolean;
  params: { id: number; section: string };
  path: string;
  url: string;
};

type Variables = {
  id: string;
  offset: string;
  section: RouteSection;
  marketCode: MarketCodes;
};

type Loaded = {
  badges: boolean;
  campaign: boolean;
  campaignDeals: boolean;
  main: boolean;
  media: boolean;
  priceHistory: boolean;
  priceList: boolean;
  productProperties: boolean;
  relatedProducts: boolean;
  userActions: boolean;
  userReviews: boolean;
  deliveryOptions: boolean;
};

// Group on product ID, ignore section param
ProductPage.group = ({ name, match }: Group) =>
  `${name}_${match && match.params ? match.params.id : 'unknown'}`;

ProductPage.variables = ({ id, offset, section, marketCode }: Variables) => {
  return {
    id: parseInt(id, 10),
    offset: parseInt(offset, 10) || 0,
    section,
    statisticsTime: section === 'statistics' ? '1970-01-02' : undefined,
    marketCode,
  };
};

ProductPage.loadOrder = (
  { section = 'main', marketCode = undefined } = {},
  loaded = {} as Loaded,
  options = { cacheRefresh: false, initialRoute: false }
) => {
  const sectionContent = {
    main: {
      priceList: true,
    },
    properties: {
      productProperties: true,
    },
    reviews: {
      userReviews: true,
    },
    statistics: {
      priceHistory: true,
      importantDates: true,
      priceHistoryTable: true,
      priceTrend: true,
      medianPrice: true,
    },
  };

  const cacheMoreData = options.cacheRefresh
    ? {
        badges: true,
        media: true,
        relatedProducts: true,
        priceHistory: true,
      }
    : {};

  const firstLoad =
    // We need to fetch all sections data for the experiment being run in DK
    marketCode === 'dk'
      ? {
          ...sectionContent.main,
          ...sectionContent.properties,
          ...sectionContent.reviews,
          ...sectionContent.statistics,
        }
      : {
          ...sectionContent[section],
        };

  return [
    {
      main: !loaded.main,
      ...cacheMoreData,
      ...firstLoad,
    },
    {
      // Refresh price list for cached pages only
      priceList: options.initialRoute && section === 'main',
      userActions: !loaded.userActions,
      badges: !loaded.badges,
      media: !loaded.media,
      campaign: !loaded.campaign,
      relatedProducts: !loaded.relatedProducts,
      campaignDeals: !loaded.campaignDeals,
      priceHistory: !loaded.priceHistory,
    },
  ];
};

const mapStateToProps = (state: AppState) => ({
  campaignBanners: state.globals.campaignBanners,
  review: state.areas.review,
  currentModal: state.modal.current,
  me: state.areas.account.me,
});

const dispatchProps = {
  showModal: modalActions.showModal,
  hideModal: modalActions.hideModal,
};

export default connect(mapStateToProps, dispatchProps)(ProductPage);
